const hasOwnProperty = Object.prototype.hasOwnProperty;

// tslint:disable:no-any
export function isObject(obj: any): obj is { [key: string]: any } {
  return typeof obj === 'object' && obj !== null;
}

export function objectHasOwnKeys(obj: { [key: string]: any }, keys: string[]): boolean {
  if (!isObject(obj)) {
    return false;
  }
  return !keys.some((key) => !hasOwnProperty.call(obj, key));
}

export function isBoolean(obj: any): obj is boolean {
  return typeof obj === 'boolean';
}

export function isNumber(obj: any): obj is number {
  return typeof obj === 'number';
}

export function isFiniteNumber(obj: any): obj is number {
  return isNumber(obj) && Number.isFinite(obj);
}

export function isFunction(obj: any): obj is ((...args: any[]) => any) {
  return typeof obj === 'function';
}

export function isString(obj: any): obj is string {
  return typeof obj === 'string';
}

export function getTimestamp(): number {
  if (window && window.performance && typeof window.performance.now === 'function') {
    return window.performance.now();
  }
  return Date.now();
}

export function camelCaseToDashedCase(str: string): string {
  return str.replace(/([A-Z])/g, (c) => `-${c.toLowerCase()}`);
}

export function camelCaseToUnderscoreCase(str: string): string {
  return str.replace(/([A-Z])/g, (c) => `_${c.toLowerCase()}`);
}

export function jsPropertyNameToTitleCase(jsPropertyName: string): string {
  return jsPropertyName.replace(/[A-Z]/g, (c) => ' ' + c).replace(/^[a-z]/g, (c) => c.toUpperCase());
}

export async function tryGetResponseJson<T = { [key: string]: any }>(response: any) {
  if (response instanceof Response) {
    try {
      const clonedResponse = response.clone();
      const responseJson = await clonedResponse.json();
      return responseJson as T;
    } catch (error) {
      if (process.env.REACT_APP_ENV !== 'production') {
        console.log(
          `ERROR: AppUtils::tryGetResponseJson: Failed to read response body as JSON. (Content-Type: ${response.headers.get(
            'Content-Type'
          )})`
        );
        console.log(`ERROR: AppUtils::tryGetResponseJson: ${error}`);
      }
    }
  }
  return Promise.resolve(undefined);
}

export function jsonStringify(data: any, space?: string | number | undefined): string {
  try {
    return JSON.stringify(data, undefined, space);
  } catch (error) {
    if (process.env.REACT_APP_ENV !== 'production') {
      console.log('ERROR: AppUtils::jsonStringify: Failed to serialize given data.');
    }
    throw error;
  }
}

export async function waitFor<TResult>(promise: Promise<TResult>, timeout: number): Promise<TResult> {
  return new Promise<TResult>((resolve, reject) => {
    const timeoutId = setTimeout(() => reject(new Error('Timeout')), timeout);

    // tslint:disable:align
    promise.then((value) => {
      clearTimeout(timeoutId);
      resolve(value);
    }, reject);
    // tslint:enable:align
  });
}

export function debounced(delay: number, fn: (...args: any[]) => void) {
  let timerId: NodeJS.Timer | null;
  return (...args: any[]): void => {
    if (timerId) {
      clearTimeout(timerId);
    }
    timerId = setTimeout(() => {
      fn(...args);
      timerId = null;
      // tslint:disable-next-line:align
    }, delay);
  };
}
// tslint:enable:no-any
